import React from 'react';
import PageHero from './PageHero';
import { islandInfo } from '../data/landmarks';
import './InfoSection.css';
import './AbrolhosInfo.css';

const AbrolhosInfo: React.FC = () => {
  const images = [
    {
      title: 'Morning Reef (Batavia wreck site)',
      src: 'https://upload.wikimedia.org/wikipedia/commons/6/64/Batavia_Wreck_site.jpg',
      alt: 'Morning Reef, Abrolhos Islands, Western Australia — Batavia wreck site marked',
      sourceUrl: 'https://commons.wikimedia.org/wiki/File:Batavia_Wreck_site.jpg',
      author: 'Guy de la Bedoyere',
      licenseName: 'CC BY-SA 4.0',
      licenseUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    },
    {
      title: 'Where are we?',
      src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6e/Houtman_Abrolhos_location.svg/1920px-Houtman_Abrolhos_location.svg.png',
      alt: 'Locator map showing where the Houtman Abrolhos are off Western Australia',
      sourceUrl: 'https://commons.wikimedia.org/wiki/File:Houtman_Abrolhos_location.svg',
      author: 'Base map by Yarl; red dot by Hesperian',
      licenseName: 'CC BY-SA 3.0',
      licenseUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    },
    {
      title: 'Southern (Pelsaert) Group',
      src: 'https://upload.wikimedia.org/wikipedia/commons/a/aa/Pelsart_Group.jpg',
      alt: 'Satellite image of the Pelsaert Group (Southern Group) of the Houtman Abrolhos',
      sourceUrl: 'https://commons.wikimedia.org/wiki/File:Pelsart_Group.jpg',
      author: 'Hesperian (NASA World Wind / Landsat layer)',
      licenseName: 'Public domain',
      licenseUrl: 'https://commons.wikimedia.org/wiki/Public_domain',
    },
    
  ] as const;

  return (
    <div className="abrolhos-info-page">
      <PageHero title="Abrolhos Info" subtitle="A quick guide before you go" />

      <div className="abrolhos-info-content-wrapper">
        <div className="abrolhos-info-content">
          <section className="trip-card">
            <h2>About the Southern Group (Pelsaert Group)</h2>
            <p className="info-description">
              The Abrolhos are a remote island-and-reef archipelago off the Mid West coast of Western
              Australia. If you’re heading out to us, you’ll be spending your time in the <strong>Southern (Pelseart)
              Group</strong> a stunning stretch of coral cays, reef flats and turquoise lagoons.
              <br /><br />
              The Abrolhos are split into three main groups (Wallabi, Easter and Pelsaert). 
              Southern Group sits toward the southern end of the chain and is famous for worlds first ecologically sustainable fishery (Western Rock Lobster),
              rich in maritime history and outstanding biodiversity with over 90 species of birds, 190 species of coral and 400 species of fish.
            </p>
            <div className="abrolhos-media-grid">
              <figure className="abrolhos-media">
                <img src={images[1].src} alt={images[1].alt} loading="lazy" />
                <figcaption>
                  <strong>{images[1].title}</strong>
                </figcaption>
              </figure>
              <figure className="abrolhos-media">
                <img src={images[2].src} alt={images[2].alt} loading="lazy" />
                <figcaption>
                  <strong>{images[2].title}</strong>
                </figcaption>
              </figure>
            </div>
          </section>

          <section className="trip-card">
            <h2>Shipwrecks of the region</h2>
            <p className="info-description">
              The reefs and weather that make the Abrolhos so beautiful have also made this coastline one
              of Australia’s most storied shipwreck regions. A few of the best-known wrecks include:
            </p>
            <div className="trip-card-facts">
              <ul>
                <li>
                  <strong>Batavia (1629)</strong> - Dutch East India Company ship, wrecked on the reefs of
                  the Wallabi Group.
                </li>
                <li>
                  <strong>Zeewijk (1727)</strong> - Dutch East India Company ship, wrecked in the
                  Southern Group.
                </li>
                <li>
                  <strong>Vergulde Draeck (1656)</strong> - associated with the wider Mid West coast and
                  often discussed in the same regional shipwreck history.
                </li>
                <li>
                  <strong>Zuytdorp (1712)</strong> - north of the Abrolhos, but commonly included in the
                  broader “Batavia Coast” shipwreck context.
                </li>
              </ul>
            </div>
            
          </section>

          <section className="trip-card trip-card--no-ticks">
            <h2>Useful Links</h2>
            <p className="info-description">
              Fishing rules and protected areas can change, so it’s worth checking the official resources
              before you depart.
            </p>
            <ul className="abrolhos-credits">
              <li>
                <a
                  href="https://www.dpird.wa.gov.au/individuals/recreational-fishing/marine-protected-areas/houtman-abrolhos-islands/recreational-fishing-at-the-houtman-abrolhos-islands/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <strong>DPIRD — Recreational fishing at the Houtman Abrolhos Islands</strong>
                </a>
              </li>
              <li>
                <a href="https://rules.fish.wa.gov.au/" target="_blank" rel="noreferrer">
                  <strong>WA Fishing Rules</strong> (rules.fish.wa.gov.au)
                </a>
              </li>
              <li>
                <a
                  href="https://www.dpird.wa.gov.au/environment-and-sustainability/sustainable-fisheries/west-coast-rock-lobster-managed-fishery/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <strong>DPIRD — West Coast Rock Lobster Managed Fishery</strong> (MSC-certified)
                </a>
              </li>
              <li>
                <a href="https://westernrocklobster.org/" target="_blank" rel="noreferrer">
                  <strong>Western Rock Lobster</strong> (industry body)
                </a>
              </li>
            </ul>
          </section>

          <section className="trip-card trip-card--no-ticks abrolhos-footnotes">
            <h2>References</h2>
            <ul className="abrolhos-credits">
              {images.map((img) => (
                <li key={img.sourceUrl}>
                  <strong>{img.title}</strong> —{' '}
                  <a href={img.sourceUrl} target="_blank" rel="noreferrer">
                    source
                  </a>
                  {img.author ? (
                    <>
                      {' '}
                      by <span>{img.author}</span>
                    </>
                  ) : null}
                  {' '}
                  (
                  <a href={img.licenseUrl} target="_blank" rel="noreferrer">
                    {img.licenseName}
                  </a>
                  )
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AbrolhosInfo;

